import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    Output,
    ViewChild,
    inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CoreUIIconSize } from '../../../icons/core-ui-icon/core-ui-icon.component';

type DateValue = string | null;
type DateConstraint = Date | string | null;

interface CalendarDay {
    date: Date;
    label: number;
    isOutside: boolean;
}

@Component({
    selector: 'core-ui-input-date',
    templateUrl: './input-date.component.html',
    styleUrls: ['./input-date.component.scss'],
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputDateComponent),
            multi: true,
        },
    ],
})
export class InputDateComponent implements ControlValueAccessor {
    @Input() label: string = 'Fecha';
    @Input() placeholder: string = 'DD/MM/AAAA';
    @Input() disabled: boolean = false;
  @Input() inline: boolean = false;
  @Input() highlightStart?: Date | string | null;
  @Input() highlightEnd?: Date | string | null;
    @Input() hideChevron: boolean = false;
    @Input() clearable: boolean = false;
    @Input() uiMode: 'form' | 'filter' = 'form';
    @Input() iconColor: string = '#5F7081';
    @Input() sizeIcon: CoreUIIconSize = 'lg';
    @Input() readOnly: boolean = false;
    @Input() error = false;
    @Input() minDate: DateConstraint = null;
    @Input() maxDate: DateConstraint = null;

    @Output() valueChange = new EventEmitter<DateValue>();

    @ViewChild('control', { read: ElementRef }) controlRef!: ElementRef<HTMLElement>;
    @ViewChild('dropdown', { read: ElementRef }) dropdownRef!: ElementRef<HTMLElement>;
    @ViewChild('textInput', { read: ElementRef }) textInputRef!: ElementRef<HTMLInputElement>;

    dropdownStyle: Record<string, string> = {};
    isOpen = false;

    inputText: string = '';
    viewMode: 'days' | 'months' | 'years' = 'days';
    yearList: number[] = [];
    yearRangeLabel: string = '';
    monthList: { label: string; value: number }[] = [];

    private _value: DateValue = null;

    currentMonth: Date = new Date();
    monthLabel: string = '';
    monthDays: CalendarDay[] = [];
    weekDays: string[] = ['D', 'L', 'M', 'MI', 'J', 'V', 'S'];

    private onChange: (value: DateValue) => void = () => { };
    private onTouched: () => void = () => { };
    private readonly hiddenDropdownStyle: Record<string, string> = {
        position: 'fixed',
        top: '0',
        left: '0',
        right: 'auto',
        bottom: 'auto',
        zIndex: '99999',
        visibility: 'hidden',
        'pointer-events': 'none',
    };

    @Input()
    get value(): DateValue {
        return this._value;
    }
    set value(val: DateValue) {
        this.setValueFromOutside(val);
    }

    private readonly changeDetector = inject(ChangeDetectorRef);

    constructor(private elementRef: ElementRef) {
        this.buildCalendar();
        this.buildMonthList();
    }

    @HostListener('document:mousedown', ['$event'])
    onClickOutside(event: Event): void {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.closeDropdown();
        }
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.closeDropdown();
    }

    @HostListener('window:resize')
    onResize(): void {
        if (this.isOpen) this.scheduleReposition();
    }

    @HostListener('window:scroll')
    onScroll(): void {
        if (this.isOpen) this.scheduleReposition();
    }

    writeValue(value: DateValue): void {
        this.setValueFromOutside(value);
    }

    registerOnChange(fn: (value: DateValue) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onControlClick(): void {
        if (this.disabled || this.readOnly) return;
        this.toggleDropdown();
    }

    toggleDropdown(): void {
        if (this.disabled) return;
        if (this.isOpen) {
            this.closeDropdown();
            return;
        }
        this.openDropdown();
        this.onTouched();
    }

    private closeDropdown(): void {
        this.isOpen = false;
        this.viewMode = 'days';
        this.dropdownStyle = {};
    }

    private openDropdown(): void {
        this.syncCalendarToValue();
        this.dropdownStyle = { ...this.hiddenDropdownStyle };
        this.isOpen = true;
        this.viewMode = 'days';
        this.scheduleReposition();
    }

    onInputClick(): void {
        this.openDropdownIfClosed();
    }

    onInputFocus(): void {
        this.inputText = this._value ?? '';
        this.openDropdownIfClosed();
    }

    private openDropdownIfClosed(): void {
        if (this.isOpen) return;
        this.openDropdown();
        this.onTouched();
    }

    onInputChange(raw: string): void {
        let digits = raw.replace(/\D/g, '');
        let formatted = digits;
        if (digits.length > 2) formatted = digits.slice(0, 2) + '/' + digits.slice(2);
        if (digits.length > 4) formatted = formatted.slice(0, 5) + '/' + digits.slice(4);
        formatted = formatted.slice(0, 10);

        this.inputText = formatted;
        if (this.textInputRef?.nativeElement) {
            this.textInputRef.nativeElement.value = formatted;
        }

        if (formatted.length === 10) {
            const parsed = this.parseDisplayDate(formatted);
            if (parsed && this.isDateAllowed(parsed)) {
                this._value = formatted;
                this.currentMonth = new Date(parsed.getFullYear(), parsed.getMonth(), 1);
                this.buildCalendar();
                this.onChange(formatted);
                this.valueChange.emit(formatted);
            }
        }
    }

    onInputBlur(): void {
        if (!this.inputText) {
            this._value = null;
            this.onChange(null);
            this.valueChange.emit(null);
            return;
        }
        const parsed = this.parseDisplayDate(this.inputText);
        if (parsed && this.isDateAllowed(parsed)) {
            const formatted = this.formatDate(parsed);
            this._value = formatted;
            this.inputText = formatted;
            this.syncCalendarToValue();
            this.onChange(formatted);
            this.valueChange.emit(formatted);
        } else {
            this.inputText = this._value ?? '';
            if (this.textInputRef?.nativeElement) {
                this.textInputRef.nativeElement.value = this.inputText;
            }
        }
    }

    onInputEnter(): void {
        this.onInputBlur();
        this.closeDropdown();
    }

    onInputEscape(): void {
        this.inputText = this._value ?? '';
        if (this.textInputRef?.nativeElement) {
            this.textInputRef.nativeElement.value = this.inputText;
        }
        this.closeDropdown();
    }

    onSelectDay(day: CalendarDay): void {
        if (this.isDayDisabled(day)) return;

        const formatted = this.formatDate(day.date);
        this._value = formatted;
        this.inputText = formatted;
        this.closeDropdown();
        this.onChange(formatted);
        this.onTouched();
        this.valueChange.emit(formatted);
    }

    onPrevMonth(): void {
        this.currentMonth = new Date(
            this.currentMonth.getFullYear(),
            this.currentMonth.getMonth() - 1,
            1,
        );
        this.buildCalendar();
        if (this.isOpen) this.scheduleReposition();
    }

    onNextMonth(): void {
        this.currentMonth = new Date(
            this.currentMonth.getFullYear(),
            this.currentMonth.getMonth() + 1,
            1,
        );
        this.buildCalendar();
        if (this.isOpen) this.scheduleReposition();
    }

    toggleYearPicker(): void {
        if (this.viewMode === 'years') {
            this.viewMode = 'days';
            return;
        }
        if (this.viewMode === 'months') {
            this.buildYearList(this.currentMonth.getFullYear());
            this.viewMode = 'years';
            this.scheduleReposition();
            return;
        }
        this.buildYearList(this.currentMonth.getFullYear());
        this.viewMode = 'years';
        this.scheduleReposition();
    }

    onSelectYear(year: number): void {
        this.currentMonth = new Date(year, this.currentMonth.getMonth(), 1);
        this.viewMode = 'months';
        this.scheduleReposition();
    }

    onSelectMonth(monthIndex: number): void {
        this.currentMonth = new Date(this.currentMonth.getFullYear(), monthIndex, 1);
        this.buildCalendar();
        this.viewMode = 'days';
        this.scheduleReposition();
    }

    onPrevYearPage(): void {
        const firstYear = this.yearList[0];
        this.buildYearList(firstYear - 1);
        this.scheduleReposition();
    }

    onNextYearPage(): void {
        const lastYear = this.yearList[this.yearList.length - 1];
        this.buildYearList(lastYear + 1);
        this.scheduleReposition();
    }

    onPrevMonthYear(): void {
        this.currentMonth = new Date(this.currentMonth.getFullYear() - 1, this.currentMonth.getMonth(), 1);
        this.scheduleReposition();
    }

    onNextMonthYear(): void {
        this.currentMonth = new Date(this.currentMonth.getFullYear() + 1, this.currentMonth.getMonth(), 1);
        this.scheduleReposition();
    }

    private buildYearList(pivotYear: number): void {
        const start = Math.floor(pivotYear / 12) * 12;
        this.yearList = Array.from({ length: 12 }, (_, i) => start + i);
        this.yearRangeLabel = `${this.yearList[0]} – ${this.yearList[this.yearList.length - 1]}`;
    }

    private buildMonthList(): void {
        this.monthList = Array.from({ length: 12 }, (_, i) => ({
            value: i,
            label: this.capitalize(new Date(2000, i, 1).toLocaleString('es-PE', { month: 'short' })),
        }));
    }

    get displayValue(): string {
        return this._value ?? this.placeholder;
    }

    get isPlaceholder(): boolean {
        return !this._value;
    }

    get isFilled(): boolean {
        return this.uiMode === 'form' && (this.value ?? '').trim().length > 0;
    }

    get showClearButton(): boolean {
        return this.clearable && !!this._value && !this.disabled && !this.readOnly;
    }

    clearValue(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        this._value = null;
        this.inputText = '';
        this.closeDropdown();
        this.onChange(null);
        this.onTouched();
        this.valueChange.emit(null);
    }

    isSelected(date: Date): boolean {
        if (!this._value) return false;
        return this._value === this.formatDate(date);
    }

    
    isDayHighlighted(date: Date): boolean {
        if (!this.highlightStart || !this.highlightEnd) return false;
        
        let start = this.highlightStart instanceof Date ? this.highlightStart : this.parseDateString(this.highlightStart);
        let end = this.highlightEnd instanceof Date ? this.highlightEnd : this.parseDateString(this.highlightEnd);
        
        if (!start || !end) return false;
        
        const d = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        const s = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
        const e = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
        
        return d >= s && d <= e;
    }

    private parseDateString(str: string | null): Date | null {
        if (!str) return null;
        if (str.includes('/')) {
            const parts = str.split('/');
            // assume dd/mm/yy or dd/mm/yyyy
            let y = Number(parts[2]);
            if (y < 100) y += 2000;
            return new Date(y, Number(parts[1]) - 1, Number(parts[0]));
        }
        if (str.includes('-')) {
            const parts = str.split('-');
            let y = Number(parts[0]);
            if (y < 100) y += 2000;
            return new Date(y, Number(parts[1]) - 1, Number(parts[2]));
        }
        return new Date(str);
    }

    isActiveDay(date: Date): boolean {
        if (this.isSelected(date)) return true;
        return !this._value && this.isSameDay(date, new Date()) && this.isDateAllowed(date);
    }

    isDayDisabled(day: CalendarDay): boolean {
        return !this.isDateAllowed(day.date);
    }

    private formatDate(date: Date): string {
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    }

    private setValueFromOutside(value: DateValue): void {
        this._value = value;
        this.inputText = value ?? '';
        this.syncCalendarToValue();
    }

    private syncCalendarToValue(): void {
        const parsed = this._value ? this.parseDisplayDate(this._value) : null;
        const baseDate = parsed ?? new Date();
        this.currentMonth = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
        this.buildCalendar();
    }

    private parseDisplayDate(v: string): Date | null {
        const match = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!match) return null;
        const d = parseInt(match[1], 10);
        const m = parseInt(match[2], 10) - 1;
        const y = parseInt(match[3], 10);
        const date = new Date(y, m, d);
        if (date.getFullYear() !== y || date.getMonth() !== m || date.getDate() !== d) return null;
        return date;
    }

    private isDateAllowed(date: Date): boolean {
        const normalizedDate = this.startOfDay(date);
        const min = this.resolveDateConstraint(this.minDate);
        const max = this.resolveDateConstraint(this.maxDate);

        if (min && normalizedDate < min) return false;
        if (max && normalizedDate > max) return false;
        return true;
    }

    private resolveDateConstraint(value: DateConstraint): Date | null {
        if (!value) return null;
        if (value instanceof Date) return this.startOfDay(value);

        const displayDate = this.parseDisplayDate(value);
        if (displayDate) return this.startOfDay(displayDate);

        const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!isoMatch) return null;

        const y = Number(isoMatch[1]);
        const m = Number(isoMatch[2]) - 1;
        const d = Number(isoMatch[3]);
        const date = new Date(y, m, d);
        if (date.getFullYear() !== y || date.getMonth() !== m || date.getDate() !== d) {
            return null;
        }
        return this.startOfDay(date);
    }

    private startOfDay(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    private isSameDay(a: Date, b: Date): boolean {
        return a.getFullYear() === b.getFullYear()
            && a.getMonth() === b.getMonth()
            && a.getDate() === b.getDate();
    }

    private buildCalendar(): void {
        const year = this.currentMonth.getFullYear();
        const month = this.currentMonth.getMonth();

        const rawLabel = `${this.currentMonth.toLocaleString('es-PE', { month: 'long' })} ${year}`;
        this.monthLabel = this.capitalize(rawLabel);

        const firstDayOfMonth = new Date(year, month, 1);
        const startDay = firstDayOfMonth.getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const prevMonth = month === 0 ? 11 : month - 1;
        const prevYear = month === 0 ? year - 1 : year;
        const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();

        const days: CalendarDay[] = [];

        for (let i = startDay - 1; i >= 0; i--) {
            const day = daysInPrevMonth - i;
            days.push({ date: new Date(prevYear, prevMonth, day), label: day, isOutside: true });
        }

        for (let d = 1; d <= daysInMonth; d++) {
            days.push({ date: new Date(year, month, d), label: d, isOutside: false });
        }

        let nextDay = 1;
        while (days.length < 42) {
            const nextMonth = (month + 1) % 12;
            const nextYear = month === 11 ? year + 1 : year;
            days.push({ date: new Date(nextYear, nextMonth, nextDay), label: nextDay, isOutside: true });
            nextDay++;
        }

        this.monthDays = days;
    }

    private scheduleReposition(): void {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => this.repositionFixedDropdown());
        });
    }

    private repositionFixedDropdown(): void {
        const controlEl = this.controlRef?.nativeElement;
        const dropdownEl = this.dropdownRef?.nativeElement;
        if (!controlEl || !dropdownEl) return;

        const gap = 8;
        const padding = 8;
        const c = controlEl.getBoundingClientRect();
        const d = dropdownEl.getBoundingClientRect();
        const viewportW = window.innerWidth;
        const viewportH = window.innerHeight;
        const spaceBottom = viewportH - c.bottom;
        const openUp = spaceBottom < d.height + gap && c.top >= d.height + gap;

        let top = openUp ? c.top - d.height - gap : c.bottom + gap;
        let left = c.left;

        left = Math.max(padding, Math.min(left, viewportW - d.width - padding));
        top = Math.max(padding, Math.min(top, viewportH - d.height - padding));

        this.dropdownStyle = {
            position: 'fixed',
            top: `${top}px`,
            left: `${left}px`,
            right: 'auto',
            bottom: 'auto',
            zIndex: '99999',
            visibility: 'visible',
            'pointer-events': 'auto',
        };
        this.changeDetector.markForCheck();
    }

    private capitalize(text: string): string {
        if (!text) return '';
        return text.charAt(0).toUpperCase() + text.slice(1);
    }
}
